// Per-question review of a past drill — stem, options with the student's
// pick + correct highlighted, explanation. Locked, no answering.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  DrillSessionDetailResponse,
  aptitudeDrillAPI,
} from '../services/aptitudeDrillAPI';
import { track } from '../services/api';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import { displayTopic } from '../config/topicDisplayNames';
import { useTheme } from '../contexts/ThemeContext';

// Theme-aware palette. Light values are byte-for-byte the originals.
const makeColors = (dark: boolean) => ({
  primary: dark ? '#86c232' : '#4a7c10',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  correct: dark ? '#86c232' : '#16a34a',
  correctBg: dark ? 'rgba(134,194,50,0.12)' : '#dcfce7',
  wrong: dark ? '#ff6b6b' : '#dc2626',
  wrongBg: dark ? 'rgba(255,107,107,0.15)' : '#fee2e2',
  pickedNeutral: dark ? '#7cb3ff' : '#1e40af',
  pickedNeutralBg: dark ? 'rgba(93,173,226,0.12)' : '#dbeafe',
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const AptitudeReview: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const isTg = location.pathname.startsWith('/tg/');
  const historyPath = isTg ? '/tg/aptitude-drill/history' : '/aptitude-drill/history';
  const [detail, setDetail] = useState<DrillSessionDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await aptitudeDrillAPI.sessionDetail(sessionId);
        if (!cancelled) {
          setDetail(data);
          track('aptitude_review.opened', {
            session_id: sessionId,
            score_pct: data.score_pct,
          });
        }
      } catch (e: any) {
        if (e?.response?.status === 401) {
          navigate('/login');
          return;
        }
        if (e?.response?.status === 404) {
          if (!cancelled) setError('That drill isn\'t in your history.');
          return;
        }
        if (!cancelled) setError(e?.message || 'Failed to load drill details');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  if (error) {
    return (
      <div style={{ padding: 24, color: dark ? '#ff6b6b' : '#dc2626' }}>
        {error}
        <div style={{ marginTop: 12 }}>
          <button onClick={() => navigate(historyPath)}>
            Back to past drills
          </button>
        </div>
      </div>
    );
  }

  if (!detail) {
    return <div style={{ padding: 24, color: COLORS.textMuted }}>Loading…</div>;
  }

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 720,
        margin: '0 auto',
        minHeight: '100vh',
        background: COLORS.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button
          onClick={() => navigate(historyPath)}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            color: COLORS.textPrimary,
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: 20, color: COLORS.textPrimary }}>Drill review</h1>
      </div>

      <div
        style={{
          background: dark ? '#171b21' : '#fff',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 14, color: COLORS.textMuted }}>
          {formatDate(detail.completed_at || detail.started_at)}
          {detail.path_day_index ? `  ·  Day ${detail.path_day_index}` : ''}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, marginTop: 4 }}>
          {detail.strategy_title || displayTopic(detail.topic || 'Aptitude') || 'Aptitude drill'}
        </div>
        <div style={{ marginTop: 8, fontSize: 16 }}>
          Score: <strong>{detail.correct}/{detail.total}</strong>
          <span style={{ color: COLORS.textMuted }}> ({detail.score_pct}%)</span>
        </div>
      </div>

      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 16 }}>
        {detail.questions.map((q, idx) => {
          const correctLabel = q.correct_answer || '';
          const studentLabel = q.student_answer || '';
          return (
            <li
              key={q.question_id}
              style={{
                background: dark ? '#171b21' : '#fff',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.textMuted,
                  marginBottom: 8,
                }}
              >
                Q{idx + 1}
                {q.topic ? `  ·  ${displayTopic(q.topic)}` : ''}
                {q.year_ec ? `  ·  ${q.year_ec} EC` : ''}
              </div>

              <div style={{ marginBottom: 12, color: COLORS.textPrimary }}>
                <MarkdownRenderer>{q.question_text}</MarkdownRenderer>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                {(q.options || []).map((opt) => {
                  const isCorrect = opt.label === correctLabel;
                  const isPicked = opt.label === studentLabel;
                  let bg = dark ? '#171b21' : '#fff';
                  let border = COLORS.border;
                  let color = COLORS.textPrimary;
                  if (isCorrect) {
                    bg = COLORS.correctBg;
                    border = COLORS.correct;
                    color = COLORS.correct;
                  } else if (isPicked) {
                    bg = COLORS.wrongBg;
                    border = COLORS.wrong;
                    color = COLORS.wrong;
                  }
                  return (
                    <li
                      key={opt.label}
                      style={{
                        background: bg,
                        border: `1px solid ${border}`,
                        borderRadius: 8,
                        padding: '8px 12px',
                        color,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <strong style={{ minWidth: 18 }}>{opt.label}.</strong>
                      <span style={{ flex: 1 }}>
                        <MarkdownRenderer>{opt.text}</MarkdownRenderer>
                      </span>
                      {isCorrect && <span title="Correct answer">✓</span>}
                      {isPicked && !isCorrect && <span title="Your answer">✗</span>}
                      {isPicked && isCorrect && (
                        <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 4 }}>
                          (your answer)
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {q.explanation && (
                <details
                  style={{
                    marginTop: 12,
                    background: dark ? '#12161c' : COLORS.bg,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontSize: 14,
                      color: COLORS.textPrimary,
                    }}
                  >
                    Explanation
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    <MarkdownRenderer>{q.explanation}</MarkdownRenderer>
                  </div>
                </details>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default AptitudeReview;
